import type {ComponentPropsWithoutRef, ReactNode} from 'react';
import {ArrowDown, ArrowUpRight} from 'lucide-react';
import {cn} from '@chekinapp/ui';

type SectionIndexGroupProps = {
  title: ReactNode;
  hint?: ReactNode;
  variant?: 'sections' | 'links';
  'aria-label'?: string;
  className?: string;
  children: ReactNode;
};

function SectionIndexGroup({
  title,
  hint,
  variant = 'sections',
  className,
  children,
  ...props
}: SectionIndexGroupProps) {
  return (
    <nav
      data-slot="section-index"
      data-variant={variant}
      aria-label={props['aria-label'] ?? (typeof title === 'string' ? title : undefined)}
      className={cn('flex flex-col', className)}
    >
      <div className="mb-3 mt-0.5 [[data-fit~='2']_&]:mb-2 [[data-fit~='5']_&]:mb-1">
        <div className="text-[14px] font-bold leading-[19px] text-[var(--assistant-tip-title)]">
          {title}
        </div>
        {hint && (
          <div className="mt-0.5 text-[12px] leading-[17px] text-[var(--assistant-tip-description)] [[data-fit~='2']_&]:hidden">
            {hint}
          </div>
        )}
      </div>
      <ul
        className={cn(
          'm-0 flex list-none flex-col gap-0.5 p-0',
          variant === 'links'
            ? "[[data-fit~='3']_&]:grid [[data-fit~='3']_&]:grid-cols-2 [[data-fit~='3']_&]:gap-x-1"
            : "[[data-fit~='4']_&]:grid [[data-fit~='4']_&]:grid-cols-2 [[data-fit~='4']_&]:gap-x-1",
        )}
      >
        {children}
      </ul>
    </nav>
  );
}

const ROW_CLASS = cn(
  'group flex w-full min-w-0 items-center gap-3 rounded-[12px] bg-transparent px-1.5 py-[9px] text-left transition-colors',
  "[[data-fit~='2']_&]:p-1.5",
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--assistant-section-accent)]',
);

type SectionIndexItemProps = Omit<ComponentPropsWithoutRef<'button'>, 'children'> & {
  index: number;
  active?: boolean;
  children: ReactNode;
};

function SectionIndexItem({
  index,
  active,
  className,
  children,
  ...props
}: SectionIndexItemProps) {
  return (
    <li className="min-w-0">
      <button
        type="button"
        data-slot="section-index-item"
        aria-current={active ? 'location' : undefined}
        className={cn(
          ROW_CLASS,
          'hover:bg-[var(--assistant-tip-icon-bg)]',
          "[[data-fit~='4']_&]:px-1.5 [[data-fit~='4']_&]:py-1",
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            'grid size-6 flex-none place-items-center rounded-full text-[12px] font-bold transition-colors',
            active
              ? 'bg-[var(--assistant-section-accent)] text-white'
              : 'bg-[var(--assistant-tip-icon-bg)] text-[var(--assistant-tip-icon)]',
          )}
        >
          {index}
        </span>
        <span
          className={cn(
            'min-w-0 flex-1 text-[13px] font-bold leading-[18px]',
            active
              ? 'text-[var(--assistant-section-accent)]'
              : 'text-[var(--assistant-tip-title)]',
          )}
        >
          {children}
        </span>
        <ArrowDown className="size-4 flex-none text-[var(--assistant-tip-description)] [[data-fit~='4']_&]:hidden" />
      </button>
    </li>
  );
}

type SectionIndexLinkProps = Omit<ComponentPropsWithoutRef<'button'>, 'title'> & {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
};

function SectionIndexLink({
  icon,
  title,
  description,
  className,
  ...props
}: SectionIndexLinkProps) {
  return (
    <li className="min-w-0">
      <button
        type="button"
        data-slot="section-index-link"
        className={cn(
          ROW_CLASS,
          'hover:bg-[color-mix(in_srgb,var(--assistant-section-accent)_8%,transparent)]',
          "[[data-fit~='1']_&]:px-1.5 [[data-fit~='1']_&]:py-[7px] [[data-fit~='2']_&]:p-1.5 [[data-fit~='3']_&]:gap-2 [[data-fit~='3']_&]:p-[5px]",
          className,
        )}
        {...props}
      >
        {icon && (
          <span
            className={cn(
              'grid size-[34px] flex-none place-items-center rounded-[11px] bg-[var(--chekin-color-white)] text-[var(--assistant-tip-icon)] [&_svg]:size-4',
              "[[data-fit~='3']_&]:size-7 [[data-fit~='3']_&]:rounded-[9px] [[data-fit~='5']_&]:hidden",
            )}
          >
            {icon}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <strong className="block text-[13px] font-bold leading-[18px] text-[var(--assistant-tip-title)] [[data-fit~='3']_&]:text-[12px] [[data-fit~='3']_&]:leading-[15px]">
            {title}
          </strong>
          {description && (
            <span className="mt-px block text-[12px] leading-4 text-[var(--assistant-tip-description)] [[data-fit~='1']_&]:hidden">
              {description}
            </span>
          )}
        </span>
        <ArrowUpRight className="size-4 flex-none text-[var(--assistant-tip-description)] [[data-fit~='3']_&]:hidden" />
      </button>
    </li>
  );
}

const SectionIndex = Object.assign(SectionIndexGroup, {
  Item: SectionIndexItem,
  Link: SectionIndexLink,
});

export {SectionIndex};
